import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './RichTextEditor.css';
import { toast } from 'react-toastify';
import { Code2, Eye, FileText, PenLine } from 'lucide-react';
import { resolveBlogImageSrc } from '../../../../utils/blogImages';
import { sanitizeBlogHtml } from '../../../../utils/blogHtml';
import { registerQuillPdfBlot } from '../../../../utils/registerQuillPdfBlot';
import { blogPdfFileName, resolveBlogPdfUrl, sanitizeBlogPdfPath } from '../../../../utils/blogPdf';

registerQuillPdfBlot();

function previewHtmlWithPendingNotes(html) {
    const raw = typeof html === "string" ? html : "";
    if (!raw.includes("oa-pdf-pending") && !raw.includes("oa-image-pending")) return raw;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${raw}</div>`, "text/html");
    const root = doc.body.firstChild;
    if (!root) return raw;
    root.querySelectorAll(".oa-pdf-pending[data-pdf-pending-id]").forEach((el) => {
        const name = el.getAttribute("data-pdf-label") || "PDF";
        const p = doc.createElement("p");
        p.className = "oa-pdf-pending-preview-note";
        const em = doc.createElement("em");
        em.textContent = name;
        p.appendChild(em);
        p.appendChild(doc.createTextNode(" — uploads when you save"));
        el.replaceWith(p);
    });
    root.querySelectorAll(".oa-image-pending[data-image-pending-id]").forEach((el) => {
        const name = el.getAttribute("data-image-label") || "Image";
        const p = doc.createElement("p");
        p.className = "oa-image-pending-preview-note";
        const em = doc.createElement("em");
        em.textContent = name;
        p.appendChild(em);
        p.appendChild(doc.createTextNode(" — uploads when you save"));
        el.replaceWith(p);
    });
    return root.innerHTML;
}

/** Estimated reading time from word count (matches backend: 220 wpm, min 1). */
function estimateReadingMinutes(words) {
    if (!words || words < 1) return 1;
    return Math.max(1, Math.ceil(words / 220));
}

export default function RichTextEditor({ initialContent = '', onChange, pendingPdfFilesRef, pendingImageFilesRef }) {
    const quillRef = useRef(null);
    const pdfInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const [content, setContent] = useState(initialContent);
    const [wordCount, setWordCount] = useState(0);
    const [charCount, setCharCount] = useState(0);
    const [viewMode, setViewMode] = useState('write'); // write | preview | html
    const lastInitialContentRef = useRef(initialContent);

    const internalPendingRef = useRef(new Map());
    const pendingMap = pendingPdfFilesRef || internalPendingRef;
    const internalImagePendingRef = useRef(new Map());
    const pendingImageMap = pendingImageFilesRef || internalImagePendingRef;

    useEffect(() => {
        // If the loaded blog content changes (e.g. switching posts / async fetch),
        // reset the editor. During normal typing, the parent echoes the same HTML
        // back down so this will not run.
        if (lastInitialContentRef.current === initialContent) return;
        lastInitialContentRef.current = initialContent;
        setContent(initialContent || '');
    }, [initialContent]);

    /** Custom toolbar controls are icon-only — native tooltips for table + PDF. */
    useEffect(() => {
        let cancelled = false;
        let innerT;
        const tryLabel = () => {
            const editor = quillRef.current?.getEditor?.();
            const toolbar = editor?.getModule?.('toolbar')?.container;
            if (!toolbar) return false;
            const tableBtn = toolbar.querySelector('button.ql-insertTable');
            const colLeftBtn = toolbar.querySelector('button.ql-insertColumnLeft');
            const colRightBtn = toolbar.querySelector('button.ql-insertColumnRight');
            const rowAboveBtn = toolbar.querySelector('button.ql-insertRowAbove');
            const rowBelowBtn = toolbar.querySelector('button.ql-insertRowBelow');
            const pdfBtn = toolbar.querySelector('button.ql-insertPdf');
            const imageBtn = toolbar.querySelector('button.ql-insertImageUpload');
            if (tableBtn) {
                tableBtn.setAttribute('title', 'Insert table (3×3)');
                tableBtn.setAttribute('aria-label', 'Insert table');
            }
            if (colLeftBtn) {
                colLeftBtn.setAttribute('title', 'Insert column left (cursor in a table cell)');
                colLeftBtn.setAttribute('aria-label', 'Insert column left');
            }
            if (colRightBtn) {
                colRightBtn.setAttribute('title', 'Insert column right (cursor in a table cell)');
                colRightBtn.setAttribute('aria-label', 'Insert column right');
            }
            if (rowAboveBtn) {
                rowAboveBtn.setAttribute('title', 'Insert row above (cursor in a table cell)');
                rowAboveBtn.setAttribute('aria-label', 'Insert row above');
            }
            if (rowBelowBtn) {
                rowBelowBtn.setAttribute('title', 'Insert row below (cursor in a table cell)');
                rowBelowBtn.setAttribute('aria-label', 'Insert row below');
            }
            if (pdfBtn) {
                pdfBtn.setAttribute('title', 'Insert PDF (uploads when you save)');
                pdfBtn.setAttribute('aria-label', 'Insert PDF');
            }
            if (imageBtn) {
                imageBtn.setAttribute('title', 'Insert image (uploads when you save)');
                imageBtn.setAttribute('aria-label', 'Insert image');
            }
            return !!(
                tableBtn &&
                pdfBtn &&
                imageBtn &&
                colLeftBtn &&
                colRightBtn &&
                rowAboveBtn &&
                rowBelowBtn
            );
        };
        const t0 = window.setTimeout(() => {
            if (cancelled || tryLabel()) return;
            innerT = window.setTimeout(() => {
                if (!cancelled) tryLabel();
            }, 150);
        }, 0);
        return () => {
            cancelled = true;
            window.clearTimeout(t0);
            window.clearTimeout(innerT);
        };
    }, []);

    useEffect(() => {
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;

        const text = (editor.getText() || '').trim();
        const words = text ? text.split(/\s+/).length : 0;
        setWordCount(words);
        setCharCount(text.length);
    }, [content]);

    /** Older saved HTML may omit `data-pdf-label`; keep editor chip text in sync with the file name. */
    useEffect(() => {
        if (viewMode !== "write") return;
        const editor = quillRef.current?.getEditor?.();
        if (!editor?.root || !String(content).includes("oa-pdf-embed")) return;
        editor.root.querySelectorAll(".oa-pdf-embed[data-pdf-url]").forEach((el) => {
            const path = sanitizeBlogPdfPath(el.getAttribute("data-pdf-url") || "");
            if (!path) return;
            el.setAttribute("data-pdf-label", blogPdfFileName(path));
            el.setAttribute("title", "Open PDF in new tab");
        });
    }, [content, viewMode]);

    /** In editor mode, uploaded blog images need the API origin so Quill can display them. */
    useEffect(() => {
        if (viewMode !== "write") return;
        const editor = quillRef.current?.getEditor?.();
        if (!editor?.root) return;
        editor.root.querySelectorAll('img[src]').forEach((el) => {
            const rawSrc = el.getAttribute("src") || "";
            const resolved = resolveBlogImageSrc(rawSrc);
            if (resolved && resolved !== rawSrc) {
                el.setAttribute("src", resolved);
            }
        });
    }, [content, viewMode]);

    /** PDF embeds open in a new tab; images stay in the editor (no navigation). */
    useEffect(() => {
        if (viewMode !== "write") return undefined;
        const editor = quillRef.current?.getEditor?.();
        const root = editor?.root;
        if (!root) return undefined;

        const onImageClick = (e) => {
            const img = e.target?.closest?.("img");
            const pendingImage = e.target?.closest?.(".oa-image-pending[data-image-pending-id]");
            if (!img && !pendingImage) return;
            if (!root.contains(img || pendingImage)) return;
            e.preventDefault();
        };

        const onPointerDown = (e) => {
            const pendingBlock = e.target?.closest?.(".oa-pdf-pending[data-pdf-pending-id]");
            if (pendingBlock && root.contains(pendingBlock)) {
                const id = pendingBlock.getAttribute("data-pdf-pending-id");
                const file = pendingMap.current?.get(id);
                if (!file) return;
                e.preventDefault();
                e.stopPropagation();
                const blobUrl = URL.createObjectURL(file);
                window.open(blobUrl, "_blank", "noopener,noreferrer");
                window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
                return;
            }

            const block = e.target?.closest?.(".oa-pdf-embed[data-pdf-url]");
            if (!block || !root.contains(block)) return;
            const path = sanitizeBlogPdfPath(block.getAttribute("data-pdf-url") || "");
            if (!path) return;
            const url = resolveBlogPdfUrl(path) || path;
            if (!url) return;
            e.preventDefault();
            e.stopPropagation();
            window.open(url, "_blank", "noopener,noreferrer");
        };

        root.addEventListener("click", onImageClick, true);
        root.addEventListener("pointerdown", onPointerDown, true);
        return () => {
            root.removeEventListener("click", onImageClick, true);
            root.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [viewMode, content, pendingMap]);

    /** Show local preview for pending inline images. */
    useEffect(() => {
        if (viewMode !== "write") return undefined;
        const editor = quillRef.current?.getEditor?.();
        const root = editor?.root;
        if (!root) return undefined;

        root.querySelectorAll(".oa-image-pending[data-image-pending-id]").forEach((el) => {
            const id = el.getAttribute("data-image-pending-id");
            const file = id ? pendingImageMap.current?.get(id) : null;
            if (!file) return;
            let img = el.querySelector("img.oa-image-pending-thumb");
            if (!img) {
                img = document.createElement("img");
                img.className = "oa-image-pending-thumb";
                img.alt = el.getAttribute("data-image-label") || "";
                el.appendChild(img);
            }
            const nextSrc = URL.createObjectURL(file);
            if (img.dataset.blobUrl) URL.revokeObjectURL(img.dataset.blobUrl);
            img.src = nextSrc;
            img.dataset.blobUrl = nextSrc;
        });
    }, [viewMode, content, pendingImageMap]);

    const readingMinutes = useMemo(() => estimateReadingMinutes(wordCount), [wordCount]);

    const previewHtml = useMemo(
        () => sanitizeBlogHtml(previewHtmlWithPendingNotes(content)),
        [content]
    );

    const modules = useMemo(
        () => ({
            table: true,
            toolbar: {
                container: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ align: [] }],
                    ['link', 'blockquote', 'code-block'],
                    ['insertImageUpload', 'insertPdf'],
                    [
                        'insertTable',
                        'insertColumnLeft',
                        'insertColumnRight',
                        'insertRowAbove',
                        'insertRowBelow',
                        'insertPdf'
                    ],
                    ['clean']
                ],
                handlers: {
                    insertTable: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;

                        const tableModule = editor.getModule('table');
                        if (tableModule && typeof tableModule.insertTable === 'function') {
                            tableModule.insertTable(3, 3);
                            return;
                        }

                        const range = editor.getSelection(true);
                        const insertAt = range ? range.index : editor.getLength();
                        const tableHtml =
                            '<table style="border-collapse: collapse; width: 100%; margin: 8px 0;">' +
                            '<tbody>' +
                            '<tr><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td></tr>' +
                            '<tr><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td></tr>' +
                            '</tbody></table><p><br/></p>';

                        editor.clipboard.dangerouslyPasteHTML(insertAt, tableHtml, 'user');
                    },
                    insertColumnLeft: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const tableModule = editor.getModule('table');
                        if (!tableModule?.getTable || !tableModule.insertColumnLeft) {
                            toast.error('Add column is only available with the table editor enabled.');
                            return;
                        }
                        const [, , cell] = tableModule.getTable();
                        if (cell == null) {
                            toast.info('Click inside a table cell, then insert a column.');
                            return;
                        }
                        tableModule.insertColumnLeft();
                    },
                    insertColumnRight: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const tableModule = editor.getModule('table');
                        if (!tableModule?.getTable || !tableModule.insertColumnRight) {
                            toast.error('Add column is only available with the table editor enabled.');
                            return;
                        }
                        const [, , cell] = tableModule.getTable();
                        if (cell == null) {
                            toast.info('Click inside a table cell, then insert a column.');
                            return;
                        }
                        tableModule.insertColumnRight();
                    },
                    insertRowAbove: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const tableModule = editor.getModule('table');
                        if (!tableModule?.getTable || !tableModule.insertRowAbove) {
                            toast.error('Add row is only available with the table editor enabled.');
                            return;
                        }
                        const [, , cell] = tableModule.getTable();
                        if (cell == null) {
                            toast.info('Click inside a table cell, then insert a row.');
                            return;
                        }
                        tableModule.insertRowAbove();
                    },
                    insertRowBelow: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const tableModule = editor.getModule('table');
                        if (!tableModule?.getTable || !tableModule.insertRowBelow) {
                            toast.error('Add row is only available with the table editor enabled.');
                            return;
                        }
                        const [, , cell] = tableModule.getTable();
                        if (cell == null) {
                            toast.info('Click inside a table cell, then insert a row.');
                            return;
                        }
                        tableModule.insertRowBelow();
                    },
                    insertPdf: () => {
                        pdfInputRef.current?.click();
                    },
                    insertImageUpload: () => {
                        imageInputRef.current?.click();
                    }
                }
            }
        }),
        []
    );

    const formats = [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'background',
        'list',
        'bullet',
        'align',
        'link',
        'image',
        'blockquote',
        'code-block',
        'table',
        'table-row',
        'table-cell',
        'oaPdf',
        'oaPdfPending',
        'oaImagePending'
    ];

    const handlePdfFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!String(file.name).toLowerCase().endsWith(".pdf")) {
            toast.error("Please choose a PDF file.");
            return;
        }
        const editor = quillRef.current?.getEditor();
        if (!editor) {
            toast.error("Editor is not ready yet.");
            return;
        }
        const id = crypto.randomUUID();
        pendingMap.current.set(id, file);
        const range = editor.getSelection(true);
        const insertAt = range ? range.index : editor.getLength();
        editor.insertEmbed(
            insertAt,
            "oaPdfPending",
            JSON.stringify({ id, name: file.name }),
            "user"
        );
        editor.setSelection(insertAt + 1, 0, "user");
        toast.success("PDF added — it uploads when you save the post.");
    };

    const handleImageFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        const isAllowed = /^image\/(png|jpeg|jpg|gif|webp|svg\+xml)$/i.test(String(file.type || ""));
        if (!isAllowed) {
            toast.error("Please choose a JPG, PNG, GIF, WEBP, or SVG image.");
            return;
        }

        const editor = quillRef.current?.getEditor();
        if (!editor) {
            toast.error("Editor is not ready yet.");
            return;
        }

        const id = crypto.randomUUID();
        pendingImageMap.current.set(id, file);
        const range = editor.getSelection(true);
        const insertAt = range ? range.index : editor.getLength();
        editor.insertEmbed(
            insertAt,
            "oaImagePending",
            JSON.stringify({ id, name: file.name }),
            "user"
        );
        editor.insertText(insertAt + 1, "\n", "user");
        editor.setSelection(insertAt + 2, 0, "user");
        toast.success("Image added — it uploads when you save the post.");
    };

    const handleEditorChange = (html, _delta, source, editor) => {
        // Keep Quill's raw HTML in state so typing (especially spaces) behaves correctly.
        setContent(html);
        // IMPORTANT: send raw HTML upward; normalize at save-time (storage), not on each keystroke.
        if (onChange) onChange(html);

        const text = (editor.getText() || '').trim();
        const words = text ? text.split(/\s+/).length : 0;
        setWordCount(words);
        setCharCount(text.length);
    };

    return (
        <div className="rte-container">
            <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="rte-pdf-file-input"
                aria-hidden="true"
                tabIndex={-1}
                onChange={handlePdfFile}
            />
            <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                className="rte-pdf-file-input"
                aria-hidden="true"
                tabIndex={-1}
                onChange={handleImageFile}
            />
            <div className="rte-max-width">
                <div className="rte-shell">
                    <header className="rte-section-head">
                        <div className="rte-section-head-icon" aria-hidden>
                            <FileText size={22} strokeWidth={2} />
                        </div>
                        <div className="rte-section-head-copy">
                            <p className="rte-section-eyebrow">Body</p>
                            <h2 className="rte-section-title">Content editor</h2>
                            <p className="rte-section-lede">
                                Write the full article here. Use the toolbar for headings, lists, tables, images, and PDFs.
                            </p>
                        </div>
                    </header>
                    <div className="rte-main-container">
                        <div className="rte-toolbar-section">
                            <div className="rte-mode-tabs" role="tablist" aria-label="Editor view">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={viewMode === 'write'}
                                    className={`rte-mode-tab ${viewMode === 'write' ? 'is-active' : ''}`}
                                    onClick={() => setViewMode('write')}
                                >
                                    <PenLine size={15} strokeWidth={2} aria-hidden />
                                    <span>Write</span>
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={viewMode === 'preview'}
                                    className={`rte-mode-tab ${viewMode === 'preview' ? 'is-active' : ''}`}
                                    onClick={() => setViewMode('preview')}
                                >
                                    <Eye size={15} strokeWidth={2} aria-hidden />
                                    <span>Preview</span>
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={viewMode === 'html'}
                                    className={`rte-mode-tab ${viewMode === 'html' ? 'is-active' : ''}`}
                                    onClick={() => setViewMode('html')}
                                >
                                    <Code2 size={15} strokeWidth={2} aria-hidden />
                                    <span>HTML</span>
                                </button>
                            </div>
                        </div>

                        <div className="rte-editor-container">
                            <div
                                className={`rte-editor-col ${viewMode !== 'write' ? 'rte-panel--hidden' : ''}`}
                                role="tabpanel"
                                aria-hidden={viewMode !== 'write'}
                            >
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={content}
                                    onChange={handleEditorChange}
                                    modules={modules}
                                    formats={formats}
                                    className="rte-quill"
                                />
                            </div>

                            <div
                                className={`rte-editor-col ${viewMode !== 'preview' ? 'rte-panel--hidden' : ''}`}
                                role="tabpanel"
                                aria-hidden={viewMode !== 'preview'}
                            >
                                <div className="rte-preview ql-snow">
                                    <div
                                        className="ql-editor rte-preview-content rte-prose"
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                    />
                                </div>
                            </div>

                            <div
                                className={`rte-editor-col ${viewMode !== 'html' ? 'rte-panel--hidden' : ''}`}
                                role="tabpanel"
                                aria-hidden={viewMode !== 'html'}
                            >
                                <textarea
                                    value={content}
                                    readOnly
                                    className="rte-source-code"
                                    spellCheck={false}
                                    aria-label="HTML source (read only)"
                                />
                            </div>
                        </div>

                        <div className="rte-status-bar">
                            <div className="rte-stat-group">
                                <div className="rte-stat-item">
                                    <span>Words</span>
                                    <span className="rte-stat-value">{wordCount}</span>
                                </div>
                                <div className="rte-stat-item">
                                    <span>Characters</span>
                                    <span className="rte-stat-value">{charCount}</span>
                                </div>
                            </div>
                            <div className="rte-stat-item rte-stat-item--read">
                                <span>Read time</span>
                                <span className="rte-stat-value">
                                    {readingMinutes} min{readingMinutes === 1 ? '' : 's'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
